// Monthly calendar of upcoming jobs — accepted supplier jobs (emerald) and any
// other task with a start date (sky), so the crew sees what's coming.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, BadgeCheck, CalendarDays } from "lucide-react";
import { useYacht } from "../../context/YachtContext";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { PageHeader, StatusBadge } from "./Dashboard";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function monthLabel(d) {
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function ymd(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// 6-week grid (Mon-start) covering the given month.
function monthGrid(anchor) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - ((first.getDay() + 6) % 7));
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

export default function TaskCalendar() {
  const { activeYacht } = useYacht();
  const [anchor, setAnchor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!activeYacht?.id) { setRows([]); return; }
    let alive = true;
    api.get(`/maintenance?yacht_id=${activeYacht.id}`)
      .then(({ data }) => { if (alive) setRows(data || []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [activeYacht?.id]);

  // Events keyed by YYYY-MM-DD. Jobs with an end_date span every day in the
  // range (role start/mid/end) so they render as a continuous multi-day bar.
  const eventsByDay = useMemo(() => {
    const map = {};
    const push = (key, ev, role) => { (map[key] = map[key] || []).push({ ev, role }); };
    rows.forEach((r) => {
      const sd = (r.start_date || "").slice(0, 10);
      if (!sd || r.deleted_at || r.status === "cancelled") return;
      const ed = (r.end_date || "").slice(0, 10);
      if (ed && ed > sd) {
        const cur = new Date(sd + "T00:00:00");
        const end = new Date(ed + "T00:00:00");
        let guard = 0;
        while (cur <= end && guard < 120) {
          const key = ymd(cur);
          push(key, r, key === sd ? "start" : key === ed ? "end" : "mid");
          cur.setDate(cur.getDate() + 1);
          guard += 1;
        }
      } else {
        push(sd, r, "single");
      }
    });
    return map;
  }, [rows]);

  const days = useMemo(() => monthGrid(anchor), [anchor]);
  const todayStr = ymd(new Date());
  const shift = (n) => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + n, 1));

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="task-calendar-page">
      <PageHeader
        overline={activeYacht?.name || "Calendar"}
        title="Calendar"
        right={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { const d = new Date(); d.setDate(1); setAnchor(d); }} data-testid="calendar-today-btn">
              Today
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(-1)} data-testid="calendar-prev-btn">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-sm font-medium min-w-[9.5rem] text-center" data-testid="calendar-month-label">{monthLabel(anchor)}</div>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(1)} data-testid="calendar-next-btn">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-4 text-xs text-zinc-600">
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500 inline-block" /> Accepted supplier job</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-sky-500 inline-block" /> Scheduled task</span>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
        <div className="grid grid-cols-7 border-b border-zinc-200 bg-zinc-50">
          {WEEKDAYS.map((w) => (
            <div key={w} className="px-2 py-2 text-[11px] uppercase tracking-wider text-zinc-500 text-center">{w}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d, i) => {
            const key = ymd(d);
            const inMonth = d.getMonth() === anchor.getMonth();
            const isToday = key === todayStr;
            const events = eventsByDay[key] || [];
            return (
              <div
                key={i}
                className={`min-h-[92px] border-b border-r border-zinc-100 p-1.5 ${inMonth ? "bg-white" : "bg-zinc-50/60"}`}
                data-testid={`calendar-day-${key}`}
              >
                <div className={`text-[11px] mb-1 ${isToday
                  ? "inline-flex h-5 w-5 items-center justify-center rounded-full bg-ink text-white font-medium"
                  : inMonth ? "text-zinc-700" : "text-zinc-400"}`}>
                  {d.getDate()}
                </div>
                <div className="space-y-1">
                  {events.slice(0, 3).map(({ ev, role }) => {
                    const accepted = !!ev.assigned_supplier;
                    // Bars run edge-to-edge through mid cells; text shows on
                    // the start day and again at each week's first column.
                    const showText = role === "single" || role === "start" || i % 7 === 0;
                    const shape =
                      role === "start" ? "rounded-l -mr-1.5 w-[calc(100%+0.375rem)]" :
                      role === "mid" ? "rounded-none -mx-1.5 w-[calc(100%+0.75rem)]" :
                      role === "end" ? "rounded-r -ml-1.5 w-[calc(100%+0.375rem)]" : "rounded w-full";
                    return (
                      <button
                        key={`${ev.id}-${key}`}
                        type="button"
                        onClick={() => setSelected(ev)}
                        className={`block text-left px-1.5 py-1 text-[10px] leading-tight truncate transition hover:brightness-95 ${shape} ${
                          accepted ? "bg-emerald-100 text-emerald-900" : "bg-sky-100 text-sky-900"}`}
                        data-testid={`calendar-event-${ev.id}`}
                        title={`${ev.title}${accepted ? ` · ${ev.assigned_supplier.supplier_name}` : ""}${ev.end_date ? ` · ${(ev.start_date || "").slice(0, 10)} → ${(ev.end_date || "").slice(0, 10)}` : ""}`}
                      >
                        {/* Same content on every segment (hidden off-start) so the bar height stays constant */}
                        <span className={showText ? undefined : "opacity-0 select-none"}>
                          <span className="font-medium">{ev.title}</span>
                          {accepted && <span className="block truncate opacity-75">{ev.assigned_supplier.supplier_name}</span>}
                        </span>
                      </button>
                    );
                  })}
                  {events.length > 3 && (
                    <div className="text-[10px] text-zinc-500 px-1.5">+{events.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {!Object.keys(eventsByDay).length && (
        <div className="text-center text-sm text-zinc-500 py-8" data-testid="calendar-empty">
          <CalendarDays className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
          No scheduled jobs with a start date yet. Accepted supplier quotes with a start date appear here automatically.
        </div>
      )}

      {selected && (
        <Dialog open onOpenChange={(o) => { if (!o) setSelected(null); }}>
          <DialogContent className="max-w-md" data-testid="calendar-event-modal">
            <DialogHeader>
              <DialogTitle>{selected.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <StatusBadge value={selected.status} />
                {selected.assigned_supplier && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[11px]">
                    <BadgeCheck className="h-3 w-3" /> Accepted job
                  </span>
                )}
              </div>
              <div><span className="text-zinc-500">Work starts:</span> {(selected.start_date || "").slice(0, 10)}</div>
              {selected.end_date && <div><span className="text-zinc-500">Work ends:</span> {(selected.end_date || "").slice(0, 10)}</div>}
              {selected.due_date && <div><span className="text-zinc-500">Deadline:</span> {(selected.due_date || "").slice(0, 10)}</div>}
              {selected.assigned_supplier && (
                <div><span className="text-zinc-500">Supplier:</span> {selected.assigned_supplier.supplier_name}
                  {selected.assigned_supplier.amount != null && (
                    <span className="text-zinc-500"> · {selected.assigned_supplier.currency} {selected.assigned_supplier.amount}</span>
                  )}
                </div>
              )}
              {selected.description && <p className="text-zinc-600 whitespace-pre-wrap">{selected.description}</p>}
            </div>
            <div className="flex justify-end">
              <Button asChild size="sm" className="bg-ink hover:bg-black" data-testid="calendar-event-open-task">
                <Link to="/app/tasks">Open in Tasks</Link>
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
